import React from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Alert, AlertTitle, AlertDescription } from '@/components/ui/alert';
import { AlertTriangle, RefreshCw, LogOut } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface ErrorRecoveryProps {
  error: string;
  onRetry: () => void;
  onRefreshSession?: () => Promise<boolean>;
}

export const ErrorRecovery: React.FC<ErrorRecoveryProps> = ({ 
  error, 
  onRetry, 
  onRefreshSession 
}) => {
  const { t } = useTranslation('finances');
  const { toast } = useToast();

  const isAuthError = error.toLowerCase().includes('authentication') || 
                     error.toLowerCase().includes('session') ||
                     error.toLowerCase().includes('unauthorized');

  const handleSignOut = async () => {
    try {
      await supabase.auth.signOut();
      toast({
        title: t('errorRecovery.signedOut'),
        description: t('errorRecovery.pleaseReLogin'),
      });
    } catch (err) {
      console.error('Sign out error:', err);
    }
  };

  const handleRefreshAndRetry = async () => {
    if (onRefreshSession) {
      const refreshed = await onRefreshSession();
      if (refreshed) {
        onRetry();
      }
    }
  };

  return (
    <Alert variant="destructive" className="mb-4">
      <AlertTriangle className="h-4 w-4" />
      <AlertTitle>
        {isAuthError ? t('errorRecovery.authError') : t('errorRecovery.connectionError')}
      </AlertTitle>
      <AlertDescription className="space-y-3">
        <p>{error}</p>
        <div className="flex gap-2 flex-wrap">
          {isAuthError ? (
            <>
              {onRefreshSession && (
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={handleRefreshAndRetry}
                >
                  <RefreshCw className="mr-2 h-4 w-4" />
                  {t('errorRecovery.refreshSessionAndRetry')}
                </Button>
              )}
              <Button 
                variant="outline" 
                size="sm" 
                onClick={handleSignOut}
              >
                <LogOut className="mr-2 h-4 w-4" />
                {t('errorRecovery.reLogin')}
              </Button>
            </>
          ) : (
            <Button 
              variant="outline" 
              size="sm" 
              onClick={onRetry}
            >
              <RefreshCw className="mr-2 h-4 w-4" />
              {t('errorRecovery.retry')}
            </Button>
          )}
        </div>
      </AlertDescription>
    </Alert>
  );
};
